import { DateTime } from 'luxon';

export const newsDateString = (date: string, node_locale: string) => {
  return DateTime.fromISO(date)
    .setLocale(node_locale)
    .toLocaleString({ month: 'long', day: 'numeric', year: 'numeric' });
};
export const dateRangeMed = (from: string, to: string, node_locale: string) => {
  if (DateTime.fromISO(from).hasSame(DateTime.fromISO(to), 'day')) {
    return DateTime.fromISO(from)
      .setLocale(node_locale)
      .toLocaleString(DateTime.DATE_MED);
  }
  return `${
    DateTime.fromISO(from).year === DateTime.fromISO(to).year
      ? DateTime.fromISO(from)
          .setLocale(node_locale)
          .toLocaleString({ month: 'short', day: 'numeric' })
      : DateTime.fromISO(from)
          .setLocale(node_locale)
          .toLocaleString(DateTime.DATE_MED)
  }
        -  
        ${DateTime.fromISO(to)
          .setLocale(node_locale)
          .toLocaleString(DateTime.DATE_MED)}
        `;
};

export const dateRangeMedWithZoneOffset = (
  from: string,
  to: string,
  node_locale: string,
) => {
  const fromDate = DateTime.fromISO(from, { setZone: true }).setLocale(
    node_locale,
  );
  const toDate = DateTime.fromISO(to, { setZone: true }).setLocale(node_locale);

  if (fromDate.hasSame(toDate, 'day')) {
    return fromDate.toLocaleString(DateTime.DATE_MED);
  }

  const fromStr =
    fromDate.year === toDate.year
      ? fromDate.toLocaleString({ month: 'short', day: 'numeric' })
      : fromDate.toLocaleString(DateTime.DATE_MED);

  const toStr = toDate.toLocaleString(DateTime.DATE_MED);

  return `${fromStr} - ${toStr}`;
};

export const dateRangeLPO = (
  from: string,
  to: string,
  node_locale: string,
  fromTextString?: string,
  untilTextString?: string,
) => {
  if (from === 'Invalid DateTime' || !from) {
    return `${untilTextString} ${DateTime.fromISO(to)
      .setLocale(node_locale)
      .toLocaleString(DateTime.DATE_MED)}`;
  }
  if (to === 'Invalid DateTime' || !to) {
    return `${fromTextString} ${DateTime.fromISO(from)
      .setLocale(node_locale)
      .toLocaleString(DateTime.DATE_MED)}`;
  }

  if (DateTime.fromISO(from).hasSame(DateTime.fromISO(to), 'day')) {
    return DateTime.fromISO(from)
      .setLocale(node_locale)
      .toLocaleString(DateTime.DATE_MED);
  }

  return `${
    DateTime.fromISO(from).year === DateTime.fromISO(to).year
      ? DateTime.fromISO(from)
          .setLocale(node_locale)
          .toLocaleString({ month: 'short', day: 'numeric' })
      : DateTime.fromISO(from)
          .setLocale(node_locale)
          .toLocaleString(DateTime.DATE_MED)
  }
        -  
        ${DateTime.fromISO(to)
          .setLocale(node_locale)
          .toLocaleString(DateTime.DATE_MED)}`;
};

export const LocalizedTime = (time: string, node_locale: string) => {
  const tempDate = `1970-01-01T${time}`;
  const options: Intl.DateTimeFormatOptions = {
    hour: '2-digit',
    minute: '2-digit',
  };
  const localizedTime = new Date(tempDate).toLocaleTimeString(
    node_locale,
    options,
  );
  return localizedTime;
};

/**
 * parse the datetime.
 *
 * @param date - ISO 8601 datetime string (may or may not include timezone)
 * @param timezone - IANA timezone (e.g. "Europe/London") to use when `date` has no timezone
 * @returns the parsed date in ISO format with timezone
 */
export function parseDateTime(date: string, timezone?: string): DateTime {
  let parsedDate: DateTime;

  // Check if validTo includes 'Z', '+' or '-' to indicate time zone or offset
  const hasZoneInfo = /[Z+-]/.test(date);

  if (hasZoneInfo) {
    parsedDate = DateTime.fromISO(date, { setZone: true });
  } else if (timezone) {
    parsedDate = DateTime.fromISO(date, { zone: timezone });
  } else {
    // fallback to system's local timezone,
    parsedDate = DateTime.fromISO(date);
  }

  return parsedDate;
}

export function isDateTimeInFuture(date: string, timezone: string): boolean {
  const parsedDate = parseDateTime(date, timezone);
  const now = DateTime.now().setZone(parsedDate.zone.name);
  return parsedDate > now;
}

export function isDateTimeInPast(date: string, timezone: string): boolean {
  const parsedDate = parseDateTime(date, timezone);
  const now = DateTime.now().setZone(parsedDate.zone.name);
  return parsedDate < now;
}

export function isDateTimeInRange(
  start: string,
  end: string,
  timezone: string,
): boolean {
  const now = DateTime.now().setZone(timezone);
  const startDate = parseDateTime(start, timezone).setZone(timezone);
  const endDate = parseDateTime(end, timezone).setZone(timezone);

  return startDate <= now && endDate >= now;
}

